/**
 * Step 4: Job Alignment Component
 *
 * Job Alignment configuration and custom screening questions:
 * - Job Alignment card with customization (isAIGenerated, passPercentage, isMustQualify)
 * - Custom screening questions (manual questions with topic "manual_questions")
 * - Auto-save via React Hook Form
 * - Drag-and-drop reordering
 * - Quick-add suggestion list items
 * - Real-time validation
 * - Card layout with scrollable content
 */

import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Alert,
  Card,
  Col,
  InputNumber,
  message,
  Row,
  Slider,
  Space,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import React, { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import StepFooter from "../../components/StepFooter";
import { PersistenceStatus } from "../../hooks/useFormPersistence";
import { JobPostFormData } from "../../schemas/jobPostValidation";
import {
  DEFAULT_PASS_PERCENTAGE,
  MAX_PASS_PERCENTAGE,
  MIN_PASS_PERCENTAGE,
} from "../../utils/constants";
import AddScreeningModal from "./components/AddScreeningModal";
import ManualScreeningList from "./components/ManualScreeningList";
import "./index.scss";
import {
  ManualScreeningQuestionState,
  transformFormToStep4Data,
  transformStoreToStep4,
} from "./utils/formTransformers";

interface Step4ManualScreeningProps {
  currentStep: number;
  totalSteps: number;
  prompt: string;
  isGenerating: boolean;
  onPromptChange: (value: string) => void;
  onGenerate: () => void;
  onPrevious: () => void;
  onNext: () => void;
  isPreviousDisabled?: boolean;
  isNextDisabled?: boolean;
  stepTitles: string[];
  isNavigating?: boolean;
  saveStatus: PersistenceStatus;
}

const Step4ManualScreening: React.FC<Step4ManualScreeningProps> = ({
  currentStep,
  totalSteps,
  prompt,
  isGenerating,
  onPromptChange,
  onGenerate,
  onPrevious,
  onNext,
  isPreviousDisabled = false,
  isNextDisabled = false,
  stepTitles,
  isNavigating = false,
  saveStatus,
}) => {
  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<JobPostFormData>();

  // Watch form values
  const step4Data = watch("step4");
  // Watch jobAlignment directly to ensure reactivity
  const jobAlignmentValue = watch("step4.jobAlignment");
  // Watch mode to determine edit behavior
  const mode = watch("mode") as "new" | "edit" | "clone";
  const isEditMode = mode === "edit";

  // Derived state for AI Generated (simplifies logic)
  const isAIGeneratedEnabled = jobAlignmentValue?.isAIGenerated ?? false;

  // In edit mode, Job Alignment settings are always read-only
  // Users cannot add or modify Job Alignment when editing a published job post
  // This is consistent with interview rounds behavior
  const isJobAlignmentReadOnly = isEditMode;

  // Helper to check if a question ID is a temp ID (newly added)
  // Temp IDs have format: temp_${timestamp}_${random1}_${random2}
  const isTempId = (id: string | number): boolean => {
    return typeof id === "string" && id.startsWith("temp_");
  };

  // Generate unique ID with high entropy to prevent collisions
  // Uses timestamp + two random strings for better uniqueness
  const generateUniqueId = (): string => {
    const timestamp = Date.now();
    const random1 = Math.random().toString(36).slice(2, 11);
    const random2 = Math.random().toString(36).slice(2, 7);
    return `temp_${timestamp}_${random1}_${random2}`;
  };

  // Convert form data to ManualScreeningQuestionState for local management
  // In edit mode, mark existing questions with _isExisting flag
  const questions = useMemo(() => {
    if (!step4Data || !step4Data.manualScreeningQuestions) {
      return [];
    }
    const transformed = transformStoreToStep4(step4Data);

    // In edit mode, mark questions as existing if they don't have a temp ID
    // Temp IDs indicate newly added questions that haven't been saved to backend yet
    if (isEditMode) {
      return transformed.map((q) => ({
        ...q,
        _isExisting: !isTempId(q.id),
      }));
    }

    return transformed;
  }, [step4Data, isEditMode]);

  // Local UI state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] =
    useState<ManualScreeningQuestionState | null>(null);

  // Handle questions change and save to form
  const handleQuestionsChange = useCallback(
    (newQuestions: ManualScreeningQuestionState[]) => {
      const step4StoreData = transformFormToStep4Data(newQuestions);

      // If questions are added but no jobAlignment exists, create it with aiGenerated: false
      const shouldCreateJobAlignment =
        newQuestions.length > 0 && !jobAlignmentValue;

      setValue(
        "step4",
        {
          ...step4Data,
          ...step4StoreData,
          // Create jobAlignment if questions are added but it doesn't exist
          jobAlignment: shouldCreateJobAlignment
            ? {
                isAIGenerated: false,
                passPercentage: DEFAULT_PASS_PERCENTAGE,
                isMustQualify: true,
              }
            : step4Data?.jobAlignment, // Preserve existing jobAlignment
        },
        { shouldDirty: true }
      );
    },
    [setValue, step4Data, jobAlignmentValue]
  );

  // Handle add question
  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setIsModalVisible(true);
  };

  // Handle edit question
  const handleEditQuestion = (question: ManualScreeningQuestionState) => {
    setEditingQuestion(question);
    setIsModalVisible(true);
  };

  // Handle delete question
  const handleDeleteQuestion = async (id: string | number) => {
    const newQuestions = questions.filter((q) => q.id !== id);

    // Update order indices
    const updatedQuestions = newQuestions.map((q, index) => ({
      ...q,
      order: index + 1,
    }));
    handleQuestionsChange(updatedQuestions);
    message.success("Screening question deleted");

    // Trigger validation after deletion
    await trigger("step4");
  };

  // Handle suggestion click
  const handleSuggestionSelect = (suggestionText: string) => {
    // Check for duplicates
    const normalized = suggestionText.trim().toLowerCase();
    const alreadyExists = questions.some(
      (q) => q.description.trim().toLowerCase() === normalized
    );

    if (alreadyExists) {
      message.warning("This question is already added");
      return;
    }

    // Add question directly from suggestion
    const newQuestion: ManualScreeningQuestionState = {
      id: generateUniqueId(),
      description: suggestionText.trim(),
      order: questions.length + 1,
    };
    const newQuestions = [...questions, newQuestion];
    handleQuestionsChange(newQuestions);
    message.success("Screening question added successfully");
  };

  // Handle modal submit
  const handleModalSubmit = (question: ManualScreeningQuestionState) => {
    if (editingQuestion) {
      // Update existing question
      const newQuestions = questions.map((q) =>
        q.id === question.id ? question : q
      );
      handleQuestionsChange(newQuestions);
      message.success("Screening question updated successfully");
    } else {
      // Add new question
      const newQuestion: ManualScreeningQuestionState = {
        ...question,
        order: questions.length + 1,
      };
      const newQuestions = [...questions, newQuestion];
      handleQuestionsChange(newQuestions);
      message.success("Screening question added successfully");
    }
    setIsModalVisible(false);
    setEditingQuestion(null);
  };

  // Handle AI Generated switch toggle - creates/updates jobAlignment
  const handleAIGeneratedToggle = async (checked: boolean) => {
    // Guard: prevent changes in read-only mode
    if (isJobAlignmentReadOnly) return;

    if (checked) {
      // Enable AI Generated - create jobAlignment if needed
      if (!jobAlignmentValue) {
        setValue(
          "step4.jobAlignment",
          {
            isAIGenerated: true,
            passPercentage: DEFAULT_PASS_PERCENTAGE,
            isMustQualify: true,
          },
          { shouldDirty: true }
        );
      } else {
        setValue("step4.jobAlignment.isAIGenerated", true, {
          shouldDirty: true,
        });
      }
    } else {
      // Disable AI Generated
      // If manual questions exist, keep jobAlignment but with isAIGenerated = false
      // If no manual questions, remove jobAlignment entirely
      const hasManualQuestions = questions.length > 0;
      if (hasManualQuestions) {
        setValue("step4.jobAlignment.isAIGenerated", false, {
          shouldDirty: true,
        });
      } else {
        setValue("step4.jobAlignment", undefined, { shouldDirty: true });
      }
    }
    await trigger("step4");
  };

  // Handle pass percentage change - save immediately (no confirmation needed for slider adjustments)
  const handlePassPercentageChange = (value: number) => {
    // Guard: prevent changes in read-only mode
    if (isJobAlignmentReadOnly) return;

    if (!jobAlignmentValue) {
      // Create jobAlignment with the new value
      setValue(
        "step4.jobAlignment",
        {
          isAIGenerated: false,
          passPercentage: value,
          isMustQualify: true,
        },
        { shouldDirty: true }
      );
    } else {
      setValue("step4.jobAlignment.passPercentage", value, {
        shouldDirty: true,
      });
    }
  };

  // Handle must qualify toggle
  const handleMustQualifyChange = (checked: boolean) => {
    // Guard: prevent changes in read-only mode
    if (isJobAlignmentReadOnly) return;

    if (!jobAlignmentValue) {
      // Create jobAlignment with the new value
      setValue(
        "step4.jobAlignment",
        {
          isAIGenerated: false,
          passPercentage: DEFAULT_PASS_PERCENTAGE,
          isMustQualify: checked,
        },
        { shouldDirty: true }
      );
    } else {
      setValue("step4.jobAlignment.isMustQualify", checked, {
        shouldDirty: true,
      });
    }
  };

  // Get existing question descriptions for duplicate checking
  const existingQuestionDescriptions = questions.map((q) => q.description);

  return (
    <div className="step4-container">
      <Card
        className="step4-card"
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <div>
              <Typography.Title
                level={5}
                style={{ marginTop: 0, marginBottom: 4, fontWeight: 600 }}
              >
                Job Alignment
              </Typography.Title>
              <Typography.Text type="secondary">
                Configure Job Alignment assessment and add custom screening
                questions.
              </Typography.Text>
            </div>
          </div>
        }
      >
        {/* Scrollable Card Body */}
        <div className="step4-card-body">
          {/* Job Alignment Settings Section */}
          <div
            style={{
              background: "var(--ant-color-bg-layout, #f0f2f5)",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: 24,
            }}
          >
            {/* Edit Mode Read-Only Notice */}
            {isJobAlignmentReadOnly && (
              <Alert
                message="Job Alignment settings cannot be modified for published job posts."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {/* AI Generated Switch */}
            <Space align="start" style={{ width: "100%", marginBottom: 16 }}>
              <Switch
                checked={isAIGeneratedEnabled}
                onChange={handleAIGeneratedToggle}
                disabled={isJobAlignmentReadOnly}
              />
              <div>
                <Typography.Text strong>AI Generated Questions</Typography.Text>
                <br />
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: "var(--ant-font-size-sm)" }}
                >
                  {isAIGeneratedEnabled
                    ? "AI will generate questions based on job requirements."
                    : "Enable to have AI generate screening questions automatically."}
                </Typography.Text>
              </div>
            </Space>

            {/* Pass Percentage and Eliminator - Only show when Job Alignment has content */}
            {(isAIGeneratedEnabled || questions.length > 0) && (
              <div
                style={{
                  borderTop: "1px solid var(--ant-color-border)",
                  paddingTop: 16,
                  marginTop: 8,
                }}
              >
                <Row gutter={24}>
                  <Col xs={24} sm={24} md={10}>
                    <Space align="start" style={{ width: "100%" }}>
                      <Switch
                        checked={jobAlignmentValue?.isMustQualify ?? true}
                        onChange={handleMustQualifyChange}
                        disabled={isJobAlignmentReadOnly}
                      />
                      <div>
                        <Typography.Text strong>
                          Eliminator Round
                        </Typography.Text>
                        <br />
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: "var(--ant-font-size-sm)" }}
                        >
                          {jobAlignmentValue?.isMustQualify
                            ? "Candidates must qualify this round to proceed."
                            : "Candidates can proceed regardless of performance."}
                        </Typography.Text>
                      </div>
                    </Space>
                  </Col>
                  <Col xs={24} sm={24} md={14}>
                    <div style={{ marginBottom: 8 }}>
                      <Space>
                        <Typography.Text strong>
                          Pass Percentage
                        </Typography.Text>
                        <Tooltip title="Minimum percentage of questions a candidate must qualify to pass this round">
                          <InfoCircleOutlined
                            style={{ color: "var(--ant-color-text-secondary)" }}
                          />
                        </Tooltip>
                      </Space>
                    </div>
                    <Space
                      direction="vertical"
                      style={{ width: "100%" }}
                      size="small"
                    >
                      <Slider
                        min={MIN_PASS_PERCENTAGE}
                        max={MAX_PASS_PERCENTAGE}
                        value={
                          jobAlignmentValue?.passPercentage ??
                          DEFAULT_PASS_PERCENTAGE
                        }
                        onChange={handlePassPercentageChange}
                        tooltip={{ formatter: (value) => `${value}%` }}
                        disabled={isJobAlignmentReadOnly}
                      />
                      <InputNumber
                        precision={0}
                        controls={true}
                        value={
                          jobAlignmentValue?.passPercentage ??
                          DEFAULT_PASS_PERCENTAGE
                        }
                        status={
                          jobAlignmentValue?.passPercentage !== undefined &&
                          jobAlignmentValue?.passPercentage !== null &&
                          (jobAlignmentValue.passPercentage <
                            MIN_PASS_PERCENTAGE ||
                            jobAlignmentValue.passPercentage >
                              MAX_PASS_PERCENTAGE)
                            ? "error"
                            : undefined
                        }
                        onChange={(value) => {
                          if (value === null || value === undefined) {
                            return;
                          }
                          // Allow any value for real-time validation display
                          handlePassPercentageChange(value);
                        }}
                        onStep={(value, info) => {
                          // Clamp value when using controls (+/-)
                          if (
                            info.type === "up" &&
                            value > MAX_PASS_PERCENTAGE
                          ) {
                            handlePassPercentageChange(MAX_PASS_PERCENTAGE);
                            return;
                          }
                          if (
                            info.type === "down" &&
                            value < MIN_PASS_PERCENTAGE
                          ) {
                            handlePassPercentageChange(MIN_PASS_PERCENTAGE);
                            return;
                          }
                          handlePassPercentageChange(value);
                        }}
                        onKeyDown={(e) => {
                          // Handle ArrowUp/ArrowDown to respect range
                          if (e.key === "ArrowUp") {
                            e.preventDefault();
                            const currentValue =
                              jobAlignmentValue?.passPercentage ??
                              DEFAULT_PASS_PERCENTAGE;
                            if (currentValue < MAX_PASS_PERCENTAGE) {
                              handlePassPercentageChange(
                                Math.min(currentValue + 1, MAX_PASS_PERCENTAGE)
                              );
                            }
                            return;
                          }
                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            const currentValue =
                              jobAlignmentValue?.passPercentage ??
                              DEFAULT_PASS_PERCENTAGE;
                            if (currentValue > MIN_PASS_PERCENTAGE) {
                              handlePassPercentageChange(
                                Math.max(currentValue - 1, MIN_PASS_PERCENTAGE)
                              );
                            }
                            return;
                          }
                          const allowedKeys = [
                            "Backspace",
                            "Delete",
                            "Tab",
                            "Escape",
                            "Enter",
                            "ArrowLeft",
                            "ArrowRight",
                            "Home",
                            "End",
                          ];
                          if (allowedKeys.includes(e.key)) {
                            return;
                          }
                          if (
                            e.ctrlKey &&
                            ["a", "c", "x"].includes(e.key.toLowerCase())
                          ) {
                            return;
                          }
                          if (e.ctrlKey && e.key.toLowerCase() === "v") {
                            e.preventDefault();
                            return;
                          }
                          if (!/^[0-9]$/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onBlur={() => {
                          const currentValue =
                            jobAlignmentValue?.passPercentage ??
                            DEFAULT_PASS_PERCENTAGE;
                          const clamped = Math.max(
                            MIN_PASS_PERCENTAGE,
                            Math.min(MAX_PASS_PERCENTAGE, currentValue)
                          );
                          handlePassPercentageChange(clamped);
                        }}
                        addonAfter={
                          <span style={{ userSelect: "none" }}>%</span>
                        }
                        style={{ width: "100%" }}
                        disabled={isJobAlignmentReadOnly}
                      />
                      {jobAlignmentValue?.passPercentage !== undefined &&
                        jobAlignmentValue?.passPercentage !== null &&
                        (jobAlignmentValue.passPercentage <
                          MIN_PASS_PERCENTAGE ||
                          jobAlignmentValue.passPercentage >
                            MAX_PASS_PERCENTAGE) && (
                          <Typography.Text
                            type="danger"
                            style={{ fontSize: "var(--ant-font-size-sm)" }}
                          >
                            Must be between {MIN_PASS_PERCENTAGE}% and{" "}
                            {MAX_PASS_PERCENTAGE}%
                          </Typography.Text>
                        )}
                    </Space>
                  </Col>
                </Row>
              </div>
            )}
          </div>

          {/* Screening Questions Section */}
          <Typography.Title
            level={5}
            style={{ marginTop: 0, marginBottom: 16 }}
          >
            Screening Questions <small>(Optional)</small>
          </Typography.Title>

          {/* Validation Error Display */}
          {errors.step4 && (
            <div style={{ marginBottom: 16 }}>
              <Typography.Text
                type="danger"
                style={{ fontSize: "var(--ant-font-size-sm)" }}
              >
                {errors.step4.message ||
                  "Please fix the validation errors above"}
              </Typography.Text>
            </div>
          )}

          {/* Questions List with Templates */}
          <ManualScreeningList
            questions={questions}
            onEdit={handleEditQuestion}
            onDelete={handleDeleteQuestion}
            onAddQuestion={
              isJobAlignmentReadOnly ? undefined : handleAddQuestion
            }
            onSelectSuggestion={
              isJobAlignmentReadOnly ? undefined : handleSuggestionSelect
            }
            existingQuestions={existingQuestionDescriptions}
            isEditMode={isEditMode}
            readOnly={isJobAlignmentReadOnly}
          />
        </div>
        <div className="step4-card-footer">
          <StepFooter
            currentStep={currentStep}
            totalSteps={totalSteps}
            prompt={prompt}
            isGenerating={isGenerating}
            onPromptChange={onPromptChange}
            onGenerate={onGenerate}
            onPrevious={onPrevious}
            onNext={onNext}
            isPreviousDisabled={isPreviousDisabled}
            isNextDisabled={isNextDisabled}
            stepTitles={stepTitles}
            isNavigating={isNavigating}
            saveStatus={saveStatus}
          />
        </div>
      </Card>

      {/* Add/Edit Question Modal */}
      <AddScreeningModal
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingQuestion(null);
        }}
        onSubmit={handleModalSubmit}
        editingQuestion={editingQuestion}
      />
    </div>
  );
};

Step4ManualScreening.displayName = "Step4ManualScreening";

export default Step4ManualScreening;
